/**
 * MachineSectionFilter.tsx
 * BUG-2026-0121 #2 — "Be able to hide soil machines in the scenario planning
 * schedule so I can just see the rock ones."
 *
 * A compact multi-select dropdown of the Lab Sections present in the scenario's
 * machine grid. All sections are shown by default; un-checking one (e.g. "Soil")
 * hides every machine in that section from the grid. The selection is owned by
 * the parent (Phase1MachineGrid), which persists it to localStorage.
 *
 * Presentation-only: it neither fetches nor mutates — it just reports which
 * sections the planner wants visible.
 */
import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import type { LabSection } from '../../../../types/schedule';
import { LAB_SECTION_OPTIONS } from '../MachineScheduleFilters';

interface MachineSectionFilterProps {
  /** Distinct lab sections present among the current capable machines. */
  sections: LabSection[];
  /** Sections currently hidden from the grid. */
  hidden: Set<LabSection>;
  /** Called with the next hidden-set whenever the planner toggles a section. */
  onChange: (hidden: Set<LabSection>) => void;
}

/** Order present sections by the canonical LAB_SECTION_OPTIONS sequence. */
function orderSections(sections: LabSection[]): LabSection[] {
  const present = new Set(sections);
  return LAB_SECTION_OPTIONS.filter((s) => present.has(s));
}

export const MachineSectionFilter: React.FC<MachineSectionFilterProps> = ({
  sections,
  hidden,
  onChange,
}) => {
  const ordered = orderSections(sections);

  // Nothing to filter when the grid has a single section (or none).
  if (ordered.length <= 1) return null;

  // The Select's `value` is the set of VISIBLE sections (everything not hidden).
  const visible = ordered.filter((s) => !hidden.has(s));

  const handleChange = (event: SelectChangeEvent<LabSection[]>) => {
    const raw = event.target.value;
    const nextVisible = new Set(
      (typeof raw === 'string' ? (raw.split(',') as LabSection[]) : raw),
    );
    // Hidden = every present section the planner did NOT keep visible.
    const nextHidden = new Set<LabSection>(ordered.filter((s) => !nextVisible.has(s)));
    onChange(nextHidden);
  };

  const renderValue = (selected: LabSection[]): string => {
    if (selected.length === ordered.length) return 'All sections';
    if (selected.length === 0) return 'None';
    return selected.join(', ');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="scenario-machine-section-filter-label" sx={{ fontSize: '0.78rem' }}>
          Show sections
        </InputLabel>
        <Select
          labelId="scenario-machine-section-filter-label"
          multiple
          value={visible}
          onChange={handleChange}
          input={<OutlinedInput label="Show sections" />}
          renderValue={renderValue}
          sx={{ fontSize: '0.78rem' }}
          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
        >
          {ordered.map((section) => (
            <MenuItem key={section} value={section} dense>
              <Checkbox size="small" checked={!hidden.has(section)} />
              <ListItemText
                primary={section}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
